// Mint-TMS-RoutePrice · dữ liệu mẫu + công thức thuần tính giá TRF (theo computeTrfPrice trong script board). Chỉ đọc, không ghi DB.

export type Cargo = 'RF' | 'DC';
export type Shape = 'OW' | 'RT_RETRACE' | 'RT_LOOP';
export type PlaceKind = 'DP' | 'WH';

/** [loại, tên, nhóm/khu vực, vĩ độ, kinh độ] */
export const PL: Readonly<Record<string, readonly [PlaceKind, string, string, number, number]>> = {
  VNCLI: ['DP', 'Cát Lái', 'Cảng', 10.7693, 106.7947], DP220010: ['DP', 'TCIT Cái Mép', 'Cảng', 10.5339, 107.0344], IPL3: ['DP', 'ICD Phước Long 3', 'ICD', 10.8272, 106.7538],
  WH0002: ['WH', 'Kho Ngọc Kim Loan', 'Đồng Tháp', 10.2778, 105.5683], WH0026: ['WH', 'Kho Thành Lợi', 'Đồng Tháp', 10.2819, 105.6999], WH0004: ['WH', 'Kho Thái Sơn', 'Cần Thơ', 10.1104, 105.6983],
  WH0046: ['WH', 'Kho Tam Ngọc Cờ Đỏ', 'Cần Thơ', 10.1473, 105.5611], WH0041: ['WH', 'Kho N&P, Bến Tre', 'Vĩnh Long', 10.1707, 106.404],
};

/** km chặng theo Vietmap (mẫu); cặp chưa có → ước theo toạ độ */
const LEGKM: Readonly<Record<string, number>> = { 'VNCLI>WH0002': 169.2, 'WH0002>VNCLI': 169.5, 'WH0002>WH0026': 14.6, 'WH0026>WH0002': 14.6, 'WH0026>VNCLI': 165.1, 'VNCLI>WH0026': 164.8 };

const hav = (a: string, b: string): number => {
  const A = PL[a], B = PL[b];
  if (!A || !B) return 0;
  const r = Math.PI / 180, dLa = (B[3] - A[3]) * r, dLo = (B[4] - A[4]) * r;
  const x = Math.sin(dLa / 2) ** 2 + Math.cos(A[3] * r) * Math.cos(B[3] * r) * Math.sin(dLo / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};
export const legKm = (a: string, b: string): number => LEGKM[`${a}>${b}`] ?? Math.round(hav(a, b) * 1.22 * 10) / 10;

export interface Setting { k: string; vcBasic: number; vcUnit: number; sfEmpty: number; sfFull: number; salB: number; oth: number; stopTot: number; stopDrv: number }
export const SET: Readonly<Record<Cargo, Setting>> = {
  RF: { k: 'RF', vcBasic: 450000, vcUnit: 6500, sfEmpty: 30, sfFull: 38, salB: 300000, oth: 250000, stopTot: 1000000, stopDrv: 200000 },
  DC: { k: 'Khô', vcBasic: 400000, vcUnit: 6000, sfEmpty: 28, sfFull: 35, salB: 250000, oth: 150000, stopTot: 1000000, stopDrv: 200000 },
};
/** bậc lương theo km · [bậc, từ km, đ/km] */
export type Tier = readonly [string, number, number];
export const TIERS: readonly Tier[] = [['Tier1', 0, 4500], ['Tier2', 36, 3800], ['Tier3', 101, 3300], ['Tier4', 151, 3000], ['Tier5', 201, 2800], ['Tier6', 301, 2600]];
export const FUELNOW = { px: 19850, at: '21/Oct/2026' };

export interface TollDef { id: string; name: string; full: string; he: 'MO' | 'KIN'; fare: number; month?: boolean; back?: number }
export const TOLLS: readonly TollDef[] = [
  { id: 'PM', name: 'Phú Mỹ', full: 'Trạm Phú Mỹ', he: 'MO', fare: 180000 },
  { id: 'NVL', name: 'Nguyễn Văn Linh', full: 'Nguyen Van Linh Toll Plaza', he: 'MO', fare: 160000, month: true },
  { id: 'C12', name: 'Km51 ⇄ Cai Lậy', full: 'Km51 → Cai Lậy (CORR12) · Cai Lậy → Km51 (CORR12)', he: 'KIN', fare: 270000, back: 270000 },
];
export interface Saved { price: number; at: string; by: string; fuelPx: number; fares: Readonly<Record<string, number>> }
export const SAVED0: Saved = { price: 9800000, at: '14/Oct/2026 16:20', by: 'PHONG', fuelPx: 19300, fares: { NVL: 140000 } };
export const HIST0: readonly (readonly [number, string, string, number])[] = [
  [9800000, '14/Oct/2026 16:20', 'PHONG', 19300], [9700000, '02/Oct/2026 10:05', 'PHONG', 19050], [9500000, '15/Sep/2026 09:30', 'DAVID', 18600], [9300000, '20/Aug/2026 14:40', 'PHONG', 18100],
];
export const ROUTE_CODE = 'RRF0002';
export const NOW_AT = '22/Oct/2026 10:30';
export const PTS0: readonly string[] = ['VNCLI', 'WH0002', 'VNCLI'];

export const clampEr = (x: number): number => Math.min(0.9, Math.max(0.05, x));

export const autoShapeOf = (pts: readonly string[]): Shape =>
  pts.length >= 2 && PL[pts[0]!]?.[0] === 'DP' && PL[pts[pts.length - 1]!]?.[0] === 'DP' ? 'RT_LOOP' : 'OW';

export const defLoads = (n: number, auto: Shape): boolean[] => Array.from({ length: n }, (_, i) => !(auto === 'RT_LOOP' && i === 0));

export interface TollLine extends TollDef { f: number; qty: number; auto: number; amt: number }
export function vetcOf(shape: Shape, fares: Readonly<Record<string, number>> | null, qo: Readonly<Record<string, number>>): TollLine[] {
  const rt = shape !== 'OW';
  return TOLLS.map((x) => {
    const f = fares?.[x.id] ?? x.fare;
    if (x.he === 'KIN') return { ...x, f, qty: rt ? 2 : 1, auto: rt ? 2 : 1, amt: f + (rt ? x.back ?? 0 : 0) };
    const auto = rt ? 2 : 1;
    const qty = qo[x.id] ?? auto;
    return { ...x, f, qty, auto, amt: f * qty };
  });
}

export interface CalcIn { cargo: Cargo; shape: Shape; km: number; er: number; nPts: number; fuelPx: number; fares: Readonly<Record<string, number>> | null; qo?: Readonly<Record<string, number>> }
export function calc(o: CalcIn) {
  const S = SET[o.cargo];
  const runKm = o.shape === 'RT_RETRACE' ? o.km * 2 : o.km;
  const tierKm = o.shape === 'RT_LOOP' ? o.km / 2 : o.km;
  const tier = [...TIERS].filter((x) => x[1] <= tierKm).sort((a, b) => b[1] - a[1])[0] ?? TIERS[0]!;
  const er = o.shape === 'RT_RETRACE' ? 0.5 : clampEr(o.er);
  const emptyKm = runKm * er, loadedKm = runKm * (1 - er);
  const fuelL = (loadedKm * S.sfFull + emptyKm * S.sfEmpty) / 100;
  const fuelCost = fuelL * o.fuelPx;
  const vehCost = S.vcUnit * runKm + S.vcBasic;
  const extra = Math.max(0, o.nPts - 3);
  const stopDrv = extra * S.stopDrv, stopCo = extra * Math.max(0, S.stopTot - S.stopDrv);
  const salBase = Math.round((S.salB + tier[2] * runKm) / 100000) * 100000;
  const sal = salBase + stopDrv;
  const vt = vetcOf(o.shape, o.fares, o.qo ?? {});
  const vetc = Math.round(vt.reduce((a, x) => a + x.amt, 0));
  const tot = sal + fuelCost + vehCost + vetc + S.oth;
  const rule = tierKm <= 35 ? 'le35' : tierKm <= 100 ? 'le100' : 'gt100';
  const tpBase = rule === 'le35' ? 1500000 : rule === 'le100' ? 2000000 : salBase * 1.7;
  const tp = tpBase + stopCo;
  const raw = tot + tp;
  const price = Math.round(raw / 100000) * 100000;
  const margin = price > 0 ? (price - tot) / price : 0;
  return { runKm, tierKm, tier, er, emptyKm, loadedKm, fuelL, fuelCost, vehCost, extra, stopDrv, stopCo, salBase, sal, vt, vetc, tot, rule, tpBase, tp, raw, price, margin, fuelPx: o.fuelPx };
}
export type Calc = ReturnType<typeof calc>;

const group = (v: number): string => String(Math.round(Math.abs(v))).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
/** 1.234.567 (dấu − thật cho số âm) */
export const th = (n: number): string => (n < 0 ? '−' : '') + group(n);
export const d1 = (n: number): string => (Math.round(n * 10) / 10).toFixed(1).replace('.', ',');
export const d2 = (n: number): string => (Math.round(n * 100) / 100).toFixed(2).replace('.', ',');
export const pc1 = (x: number): string => `${(Math.round(x * 1000) / 10).toFixed(1).replace('.', ',')}%`;
/** số km kiểu VN: 1.234,5 */
export const parseKm = (s: string): number => { const v = Number(s.trim().replace(/\./g, '').replace(',', '.')); return Number.isFinite(v) ? v : NaN; };
export const parseEr = (s: string): number => { const v = Number(s.trim().replace(',', '.')); return Number.isFinite(v) ? v : NaN; };
